import { useTranslation } from 'react-i18next';
import { tradeLabel } from '@utils/tradeLabel';
import type { IFinanceCosts, EquipmentStatus } from '@interfaces/IFinance';
import { fmtUsd, fmtUsdNoCents } from '../format';
import { KpiCard } from './KpiCard';
import styles from './CostsTab.module.css';

interface ICostsTabProps {
  costs: IFinanceCosts;
}

export function CostsTab({ costs }: ICostsTabProps) {
  const { t } = useTranslation();

  return (
    <>
      <div className={styles.kpiRow}>
        <KpiCard
          value={fmtUsdNoCents(costs.kpis.laborCostToday)}
          label={t('finance.costs.laborCostToday')}
          valueTone="blue"
        />
        <KpiCard
          value={fmtUsdNoCents(costs.kpis.equipmentCostToday)}
          label={t('finance.costs.equipmentCostToday')}
          valueTone="amber"
        />
        <KpiCard
          value={fmtUsdNoCents(costs.kpis.totalCostToday)}
          label={t('finance.costs.totalCostToday')}
          valueTone="navy"
        >
          <div className={styles.liveTrack}>
            <div className={styles.liveFill} style={{ width: `${liveFillPct(costs)}%` }} />
          </div>
          <button type="button" className={styles.liveLink}>
            {t('finance.costs.liveAccumulation')}
          </button>
        </KpiCard>
      </div>

      <div className={styles.tablesRow}>
        <section className={styles.panel}>
          <h2 className={styles.panelTitle}>{t('finance.costs.laborCostByTrade')}</h2>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('finance.costs.colTrade')}</th>
                <th className={styles.alignRight}>{t('finance.costs.colWorkers')}</th>
                <th className={styles.alignRight}>{t('finance.costs.colRatePerHour')}</th>
                <th className={styles.alignRight}>{t('finance.costs.colHours')}</th>
                <th className={styles.alignRight}>{t('finance.costs.colCost')}</th>
              </tr>
            </thead>
            <tbody>
              {costs.labor.map((row) => (
                <tr key={row.trade}>
                  <td>
                    <span className={`${styles.tradeDot} ${styles[`dot_${row.trade}`]}`} />
                    {tradeLabel(t, row.trade)}
                  </td>
                  <td className={styles.alignRight}>{row.workers}</td>
                  <td className={styles.alignRight}>${row.ratePerHour}</td>
                  <td className={styles.alignRight}>{row.hoursLabel}</td>
                  <td className={`${styles.alignRight} ${styles.bold}`}>{fmtUsd(row.cost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className={styles.panel}>
          <h2 className={styles.panelTitle}>{t('finance.costs.equipmentCostTracker')}</h2>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('finance.costs.colEquipment')}</th>
                <th className={styles.alignCenter}>{t('finance.costs.colStatus')}</th>
                <th className={styles.alignRight}>{t('finance.costs.colRatePerHourShort')}</th>
                <th className={styles.alignRight}>{t('finance.costs.colHours')}</th>
                <th className={styles.alignRight}>{t('finance.costs.colCost')}</th>
              </tr>
            </thead>
            <tbody>
              {costs.equipment.map((row) => (
                <tr key={row.id}>
                  <td>{row.name}</td>
                  <td className={styles.alignCenter}>
                    <span
                      role="img"
                      aria-label={t(equipmentStatusKey(row.status))}
                      className={`${styles.statusDot} ${
                        row.status === 'active'
                          ? styles.statusActive
                          : row.status === 'idle'
                            ? styles.statusIdle
                            : styles.statusOffline
                      }`}
                    />
                  </td>
                  <td className={styles.alignRight}>${row.ratePerHour}</td>
                  <td className={styles.alignRight}>{row.hoursLabel}</td>
                  <td className={`${styles.alignRight} ${styles.bold}`}>{fmtUsd(row.cost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>
    </>
  );
}

function equipmentStatusKey(status: EquipmentStatus): string {
  switch (status) {
    case 'active':  return 'finance.costs.statusActive';
    case 'idle':    return 'finance.costs.statusIdle';
    case 'offline': return 'finance.costs.statusOffline';
  }
}

function liveFillPct(costs: IFinanceCosts): number {
  const laborShare = costs.kpis.laborCostToday / costs.kpis.totalCostToday;
  return Math.min(100, Math.max(0, laborShare * 100));
}
